<!-- eslint-disable vue/html-self-closing -->

<template>
	<div class="search-input-container" :disabled="disabled" :size="size" :data-shape="shape">
		<div class="search-meta-wrapper" :disabled="disabled">
			<div v-if="showSearchIcon" class="search-icon">
				<Icon :name="icon" type="filled" :size="iconSizes[size]" :effects="false" />
			</div>
			<div v-if="$slots['default']" class="search-by-dropdown" :showSearchIcon="showSearchIcon">
				<!-- @slot Used to display 'Search By' dropdown-->
				<slot />
			</div>
		</div>
		<div class="search-input-box">
			<input
				ref="search-input"
				v-model="innerValue"
				:placeholder="placeholder"
				:disabled="disabled"
				:data-qa-field="dataQaField"
				@click="handleClick"
				@change="handleChange"
				@keyup="handleKeyUp"
				@focus="focusSearch"
				@blur="blurSearch"
			/>

			<svg
				:visible="showClear"
				x="0px"
				class="search-clear"
				y="0px"
				viewBox="0 0 60 60"
				xml:space="preserve"
				stroke-width="8px"
				data-qa-field="search-clear"
				@click="clearSearch"
			>
				<path class="stroke" d="M56,4L4,56" />
				<path class="stroke" d="M56,56L4,4" />
			</svg>
		</div>
		<div v-if="noResultTag" class="search-nor-result-tag">
			<Tag size="small">
				<span class="upper-case fc-warning">{{ noResultText }}</span>
			</Tag>
		</div>
		<div v-if="$slots['autocomplete']" class="search-input-autocomplete">
			<div v-if="autoCompleteHeader" class="search-input-autocomplete-header">
				<slot name="autocomplete-header">
					<p class="paragraph-3 upper-case fc-light">RECENT SEARCH</p>
				</slot>
			</div>
			<slot name="autocomplete" />
		</div>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import Icon, { IconSize } from "./../Icon/Icon.vue";
import Tag from "./../Tag/Tag.vue";

const SIZE = ["normal", "large", "small"] as const;
const SHAPE = ["rounded", "curved"] as const;

export type SearchInputSize = typeof SIZE[number];
export type SearchInputShape = typeof SHAPE[number];

/**
 * The search input helps the user to search and filter the content. Optional user can also selects the attributes to apply search over.
 */
export default defineComponent({
	name: "SearchInput",
	components: {
		Icon,
		Tag
	},
	props: {
		/**
		 * placeholder for search input
		 */
		placeholder: {
			type: String,
			default: "Search"
		},
		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},
		/**
		 * value of search box
		 */
		value: {
			type: String,
			default: null
		},
		/**
		 * showSearch icon
		 */
		showSearchIcon: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		noResultTag: {
			type: Boolean,
			default: false
		},
		/**
		 * Text to display when there is no results
		 */
		noResultText: {
			type: String,
			default: "No results"
		},
		/**
		 * 'normal' | 'large' | 'small'
		 */
		size: {
			type: String as PropType<SearchInputSize>,
			default: "normal"
			// validator(value: SearchInputSize) {
			// 	return SIZE.includes(value);
			// }
		},
		/**
		 * autocomplete header
		 */
		autoCompleteHeader: {
			type: Boolean,
			default: true
		},
		/**
		 * shape of box
		 */
		shape: {
			type: String as PropType<SearchInputShape>,
			default: "rounded"
			// validator(value: SearchInputShape) {
			// 	return SHAPE.includes(value);
			// }
		},
		icon: {
			type: String,
			default: "i-search"
		},
		dataQaField: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			showClear: false,
			iconSizes: {
				large: "medium",
				normal: "small",
				small: "10px"
			} as Record<SearchInputSize, IconSize>
		};
	},

	computed: {
		innerValue: {
			// getter
			get() {
				return this.value;
			},
			// setter
			set(newValue: string) {
				this.$emit("update:value", newValue);
			}
		}
	},

	methods: {
		blurSearch(event: FocusEvent) {
			this.showClear = false;
			this.$emit("blur", event);
		},
		focusSearch(event: FocusEvent) {
			this.showClear = true;
			this.$emit("focus", event);
		},
		handleClick(event: MouseEvent) {
			/**
			 * This event emitted when Search Input element is clicked.
			 *
			 * @event click
			 */
			this.$emit("click", event);
		},
		handleChange(event: Event) {
			/**
			 * This event emitted when Search Input element's value changed.
			 *
			 * @event change
			 */
			this.$emit("change", event);

			/**
			 * This event emitted for validation framework
			 *
			 * @event input
			 */
			this.$emit("input", event);

			/**
			 * This event emitted for 2 way binding for value property <br/>
			 * Note: Please use :value.sync for 2 way binding
			 *
			 * @event update:value
			 */
			this.$emit("update:value", this.innerValue);
		},
		clearSearch(event: MouseEvent) {
			// console.log("in clear");

			(this.$refs["search-input"] as HTMLInputElement).focus();

			this.innerValue = "";
			this.$emit("change", event);
			this.$emit("input", event);

			this.$emit("update:value", "");
			// event.stopPropagation();
		},
		handleKeyUp(event: KeyboardEvent) {
			this.$emit("input", event);

			/**
			 * keyup on Search Input box
			 *
			 *
			 * @event keyup
			 */
			this.$emit("keyup", event);
			this.$emit("update:value", this.innerValue);
		}
	}
});
</script>
<style lang="scss">
@mixin search-input(
	$color,
	$light-color,
	$background-color,
	$border-color,
	$hover,
	$focus,
	$auto-complete-hover,
	$placeholder
) {
	.search-input-container {
		background-color: $background-color;
		&[disabled] {
			&:before {
				background-color: $hover;
			}
		}

		&:focus-within {
			box-shadow: inset 0 0 0 1px $focus;
			> .search-meta-wrapper {
				> .search-by-dropdown {
					border-right: 2px solid $focus;
				}
			}
		}
		> .search-meta-wrapper {
			> .search-icon {
				> .icon-container {
					& > .icon[type="filled"] svg {
						fill: $color;
					}
				}
			}
			> .search-by-dropdown {
				border-right: 1px solid $border-color;
			}
			// &:not([disabled]) {
			// 	&:focus {
			// 		box-shadow: 0 0 0 2px $focus;
			// 	}
			// }
		}
		> .search-input-box {
			> input {
				color: $color;
				background-color: $background-color;
				border: none;
				&::placeholder {
					color: $placeholder;
				}
				// &:not([disabled]) {
				// 	&:focus {
				// 		box-shadow: inset 0 0 0 2px $focus;
				// 	}
				// }
				&:-webkit-autofill {
					-webkit-text-fill-color: $color !important;
					-webkit-box-shadow: 0 0 0 30px $background-color inset !important;
				}
			}
			> svg.search-clear {
				stroke: $light-color;
			}
		}
		// &:not([disabled]) {
		// 	&:focus {
		// 		box-shadow: 0 0 0 2px $focus;
		// 	}
		// }
		div.search-nor-result-tag {
			border-left: 1px solid $border-color;
		}

		> div.search-input-autocomplete {
			background-color: $background-color;

			// > *:not(.search-input-autocomplete-header) {
			// 	&:hover {
			// 		background-color: $auto-complete-hover;
			// 	}
			// }
		}
	}
}

.search-input-container {
	display: flex;
	align-items: center;

	position: relative;
	padding: 2px;
	max-height: 36px;

	&[data-shape="rounded"] {
		border-radius: 18px;
		&[disabled] {
			&:before {
				border-radius: 18px;
				border-radius: 18px;
			}
		}
		> .search-meta-wrapper {
			border-bottom-left-radius: 18px;
			border-top-left-radius: 18px;
		}
		> .search-input-box {
			border-top-right-radius: 18px;
			border-bottom-right-radius: 18px;
			> input {
				border-top-right-radius: 18px;
				border-bottom-right-radius: 18px;
			}
		}
	}
	&[data-shape="curved"] {
		border-radius: 4px;
		&[disabled] {
			&:before {
				border-radius: 4px;
				border-radius: 4px;
			}
		}
		> .search-meta-wrapper {
			border-bottom-left-radius: 4px;
			border-top-left-radius: 4px;
		}
		> .search-input-box {
			border-top-right-radius: 4px;
			border-bottom-right-radius: 4px;
			> input {
				border-top-right-radius: 4px;
				border-bottom-right-radius: 4px;
			}
		}
	}
	// padding-left: 12px;
	&[disabled] {
		cursor: not-allowed;
		&:before {
			content: "";
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			width: 100%;
			height: 100%;
			opacity: 0.4;
		}
	}
	> .search-meta-wrapper {
		height: 100%;
		display: inline-flex;
		align-items: center;

		// padding-left: 12px;
		position: relative;
		// overflow: hidden;

		> .search-by-dropdown {
			> div.multiselect-with-icon > div.multiselect {
				min-width: 120px;
			}
			&:not([showSearchIcon]) {
				padding-left: 4px;
				div.multiselect {
					> div.multiselect__tags {
						padding: 0px 36px 0 0px;
					}
				}
			}
		}

		> .search-icon {
			padding-left: 12px;
			height: 100%;
			display: inline-flex;
			align-items: center;
			> .icon-container {
				height: fit-content;
				width: fit-content;
			}
		}
		&:not([disabled]) {
			&:focus {
				outline: none;
			}
		}
	}
	> .search-input-box {
		flex: 1 1 auto;
		display: flex;

		align-items: center;
		position: relative;
		> input {
			width: 100%;
			font-size: 12px;
			font-family: inherit;
			font-weight: normal;
			line-height: 14px;
			padding: 8px;
			outline: none;
			padding-right: 36px;
			flex: 1 1 auto;
			&:not([disabled]) {
				&:focus {
					outline: none;
				}
			}
		}

		> svg.search-clear {
			height: 12px;
			width: 12px;
			cursor: pointer;
			opacity: 0;
			z-index: 1;
			position: absolute;
			right: 0;
			margin-right: 18px;
			&[visible] {
				opacity: 1;
			}
		}
	}
	&:not([disabled]) {
		&:focus {
			outline: none;
		}
	}

	div.search-nor-result-tag {
		padding: 0px 12px 0px 12px;
		display: flex;
		align-items: center;
	}

	div.multiselect__tags {
		background-color: transparent !important;

		.multiselect__single {
			background-color: transparent !important;
		}
	}

	&[size="large"] {
		max-height: 50px;
		border-radius: 25px;
		padding: 4px;

		&[disabled] {
			&:before {
				border-radius: 25px;
			}
		}

		> .search-meta-wrapper {
			border-bottom-left-radius: 25px;
			border-top-left-radius: 25px;
		}

		> .search-input-box {
			border-top-right-radius: 25px;
			border-bottom-right-radius: 25px;

			> input {
				border-top-right-radius: 25px;
				border-bottom-right-radius: 25px;

				font-size: 20px;
				line-height: 20px;
				padding: 11px;
				padding-right: 36px;
			}

			> svg.search-clear {
				height: 20px;
				width: 20px;
			}
		}
	}
	&[size="small"] {
		max-height: 24px;
		border-radius: 12px;
		padding: 4px;

		&[disabled] {
			&:before {
				border-radius: 12px;
			}
		}

		> .search-meta-wrapper {
			border-bottom-left-radius: 12px;
			border-top-left-radius: 12px;
			> .search-icon {
				padding-left: 8px;
				padding-right: 8px;
			}
		}

		> .search-input-box {
			border-top-right-radius: 12px;
			border-bottom-right-radius: 12px;

			> input {
				border-top-right-radius: 12px;
				border-bottom-right-radius: 12px;

				font-size: 12px;
				line-height: 12px;
				padding: 2px;
				padding-right: 24px;
			}

			> svg.search-clear {
				height: 10px;
				width: 10px;
				margin-right: 8px;
			}
		}
	}

	> div.search-input-autocomplete {
		position: absolute;
		top: calc(100% + 4px);
		left: 0px;
		right: 0px;
		z-index: 90;

		display: flex;
		max-height: 0px;
		flex-direction: column;
		transition: max-height 0.3s;
		transition-delay: 0.2s;
		overflow-y: hidden;
		border-radius: 4px;

		> div.search-input-autocomplete-header {
			padding: 12px;
		}

		> div.flow-auto-complete-results-wrapper {
			display: flex;
			flex-direction: column;

			> div.flow-auto-complete-results-category {
				&:not(:last-of-type) {
					border-bottom: 1px solid var(--dashboard-border);
				}
				display: flex;
				flex-direction: column;
				> div.flow-auto-complete-r-c-header {
					padding: 16px;
					display: flex;
					justify-content: space-between;
					> div.cat-header {
						font-size: 12px;
						line-height: 12px;
						font-weight: bold;
						color: var(--dashboard-font-light);
						text-transform: uppercase;
					}
					> div.cat-header-link {
						font-size: 12px;
						line-height: 12px;
						color: var(--dashboard-primary);
						cursor: pointer;
					}
				}
				> div.flow-auto-complete-result {
					padding-left: 24px;
					padding-top: 16px;
					padding-bottom: 16px;
					padding-right: 16px;

					display: flex;
					justify-content: space-between;
					align-items: center;
					cursor: pointer;

					> div.result-header {
						> div.result-title {
							font-size: 14px;
							line-height: 14px;
							color: var(--dashboard-font-dark);
						}
						> div.result-sub-title {
							font-size: 10px;
							line-height: 10px;
							margin-top: 8px;
							color: var(--dashboard-font-light);
						}
					}

					> div.result-type {
						display: flex;
						color: var(--dashboard-font-light);
						align-items: flex-start;
						> * + * {
							margin-left: 8px;
						}
					}

					&:hover {
						background-color: var(--dashboard-element);
					}
				}
			}
		}
	}
	&:focus-within {
		> div.search-input-autocomplete {
			// display: flex;
			max-height: 50vh;
			overflow-y: auto;
		}
	}
	div.multiselect > div.multiselect__content-wrapper {
		width: auto;
		min-width: 120px;
		max-width: 300px;
		span.multiselect__option {
			span {
				white-space: nowrap !important;
				overflow: hidden;
				text-overflow: ellipsis;
			}
			&:after {
				display: none;
			}
		}
	}
}

@include search-input(
	var(--dashboard-font),
	var(--dashboard-font-light),
	var(--dashboard-input),
	var(--dashboard-border-dark),
	var(--dashboard-element-dark),
	var(--dashboard-primary),
	var(--dashboard-border),
	var(--dashboard-input-placeholder)
);

/** Mode related usecase */
.sideout-container {
	@include search-input(
		var(--slideout-font),
		var(--slideout-font-light),
		var(--slideout-input),
		var(--slideout-border-dark),
		var(--slideout-element-dark),
		var(--slideout-primary),
		var(--slideout-border),
		var(--slideout-input-placeholder)
	);
}
</style>
